import {
  Document,
  Page,
  StyleSheet,
  Text,
  View
} from '@react-pdf/renderer'

import type { MyData, MyDataAddress } from '../types'

const c = {
  primary: '#244bcd',
  primaryDark: '#00298f',
  body: '#434368',
  strong: '#1d1d2a',
  muted: '#6b7280',
  surface: '#f4f6f9',
  cardBorder: '#e5e7eb',
  white: '#ffffff'
}

const NOT_INFORMED = 'não informado'

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 28,
    paddingBottom: 40,
    fontSize: 11,
    color: c.body,
    lineHeight: 1.4
  },
  topbar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  },
  brandName: { fontFamily: 'Helvetica-Bold', fontSize: 13, color: c.primaryDark },
  brandTag: { fontSize: 7, color: c.muted, letterSpacing: 0.5 },
  topMetaLabel: { fontSize: 7, color: c.muted, textAlign: 'right' },
  topMetaValue: { fontSize: 8.5, color: c.strong, fontFamily: 'Helvetica-Bold', textAlign: 'right' },

  hero: {
    backgroundColor: c.surface,
    borderRadius: 12,
    padding: 18,
    marginBottom: 16
  },
  heroLabel: {
    fontSize: 8,
    letterSpacing: 1,
    color: c.primary,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4
  },
  heroTitle: { fontSize: 21, fontFamily: 'Helvetica-Bold', color: c.primaryDark },

  card: {
    backgroundColor: c.white,
    borderWidth: 1,
    borderColor: c.cardBorder,
    borderRadius: 10,
    padding: 14,
    marginBottom: 12
  },
  cardTitle: {
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    color: c.strong,
    marginBottom: 8
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: c.surface,
    paddingVertical: 4
  },
  rowLabel: { fontSize: 10, color: c.muted },
  rowValue: { fontSize: 10, color: c.strong, fontFamily: 'Helvetica-Bold', maxWidth: '60%', textAlign: 'right' },
  rowValueEmpty: { fontSize: 10, color: c.muted, fontStyle: 'italic', textAlign: 'right' },
  subTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: c.body, marginTop: 6, marginBottom: 3 },
  listItem: { fontSize: 10, color: c.body, marginBottom: 2 },
  empty: { fontSize: 10, color: c.muted, fontStyle: 'italic' }
})

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  const empty = value === null || value === undefined || value === ''
  return (
    <View style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      <Text style={empty ? styles.rowValueEmpty : styles.rowValue}>
        {empty ? NOT_INFORMED : String(value)}
      </Text>
    </View>
  )
}

function formatAddress(address: MyDataAddress): string | null {
  const parts = [
    [address.logradouro, address.numero].filter(Boolean).join(', '),
    address.complemento,
    address.bairro,
    address.cidade,
    address.estado,
    address.cep,
    address.pais
  ].filter(Boolean)
  return parts.length ? parts.join(' - ') : null
}

export function MyDataPdfDocument({ data }: { data: MyData }) {
  const generatedAt = new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date())

  const p = data.dados_pessoais
  const j = data.jornadas
  const m = data.missoes
  const materials = data.biblioteca.materiais

  return (
    <Document title="Meus Dados">
      <Page size="A4" style={styles.page}>
        <View style={styles.topbar}>
          <View>
            <Text style={styles.brandName}>Missão Futuro</Text>
            <Text style={styles.brandTag}>EDUCAÇÃO EMPREENDEDORA</Text>
          </View>
          <View>
            <Text style={styles.topMetaLabel}>Exportado em</Text>
            <Text style={styles.topMetaValue}>{generatedAt}</Text>
          </View>
        </View>

        <View style={styles.hero}>
          <Text style={styles.heroLabel}>RELATÓRIO DE DADOS PESSOAIS (LGPD)</Text>
          <Text style={styles.heroTitle}>Meus Dados</Text>
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Dados pessoais</Text>
          <Row label="Nome" value={p.nome} />
          <Row label="E-mail" value={p.email} />
          <Row label="CPF" value={p.cpf} />
          <Row label="Código do responsável" value={p.codigo} />
          <Row label="Telefone" value={p.telefone} />
          <Row label="Endereço" value={formatAddress(p.endereco)} />
          <Row label="Escola" value={p.escola} />
          <Row label="Ano escolar" value={p.ano_escolar} />
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Jornadas</Text>
          <Text style={styles.subTitle}>Concluídas</Text>
          {j.concluidas.length ? (
            j.concluidas.map(journey => (
              <Text key={journey.id} style={styles.listItem}>• {journey.nome}</Text>
            ))
          ) : (
            <Text style={styles.empty}>{NOT_INFORMED}</Text>
          )}
          <Text style={styles.subTitle}>Progresso</Text>
          <Row label="Pontos" value={j.progresso.pontos} />
          <Row label="Moedas" value={j.progresso.moedas} />
          <Row label="Medalhas" value={j.progresso.medalhas} />
          <Row label="Níveis concluídos" value={j.progresso.niveis_concluidos} />
          <Row label="Desafios concluídos" value={j.progresso.desafios_concluidos} />
          <Row label="Título" value={j.progresso.titulo} />
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Missões</Text>
          <Text style={styles.subTitle}>Concluídas</Text>
          {m.concluidas.length ? (
            m.concluidas.map(mission => (
              <Text key={mission.id} style={styles.listItem}>• {mission.nome}</Text>
            ))
          ) : (
            <Text style={styles.empty}>{NOT_INFORMED}</Text>
          )}
          <Text style={styles.subTitle}>Em andamento</Text>
          {m.em_andamento.length ? (
            m.em_andamento.map(mission => (
              <Text key={mission.id} style={styles.listItem}>• {mission.nome}</Text>
            ))
          ) : (
            <Text style={styles.empty}>{NOT_INFORMED}</Text>
          )}
        </View>

        <View style={styles.card}>
          <Text style={styles.cardTitle}>Materiais baixados (Biblioteca)</Text>
          {materials.length ? (
            materials.map(material => (
              <Text key={material.id} style={styles.listItem}>• {material.nome}</Text>
            ))
          ) : (
            <Text style={styles.empty}>{NOT_INFORMED}</Text>
          )}
        </View>
      </Page>
    </Document>
  )
}
